/**
 * Truncates a string to `maxLength` characters, appending `…` if truncated.
 */
export function truncate(value: string, maxLength = 12): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value
}

/**
 * Truncates from the middle, keeping both ends. For opaque fixed-length
 * identifiers — transaction hashes, ledger addresses, token currency codes —
 * where {@link truncate} would drop the tail that actually distinguishes one
 * value from the next (two XRPL hashes routinely share a leading run).
 *
 * Returns the value untouched when eliding would not shorten it.
 *
 * @example truncateMiddle('E3FE6EA3D48F0C2B639448020EA4F03D4F4F8FFDB243A852A0F59177921B4879')
 *          // 'E3FE6EA3D4…21B4879'
 */
export function truncateMiddle(value: string, head = 10, tail = 7): string {
  if (head < 0 || tail < 0) return value
  return value.length > head + tail + 1
    ? `${value.slice(0, head)}…${value.slice(value.length - tail)}`
    : value
}

/**
 * Strips every non-digit character, returning only the digits.
 * Useful for sanitising phone numbers, OTPs, account numbers, etc.
 */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * Combines first and last name into a full name, returning a fallback if both are empty.
 */
export function fullName(
  firstName?: string | null,
  lastName?: string | null,
  fallback = '—',
): string {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim()
  return name || fallback
}

// Domain acronyms that must render all-caps rather than Title Case
// ("kyc" → "KYC", not "Kyc"). Whole-word match only.
const FORMAT_KEY_ACRONYMS = new Set([
  'KYC',
  'KYB',
  'AML',
  'PEP',
  'VASP',
  'IBAN',
  'SWIFT',
  'BIC',
  'OTP',
  'ROI',
  'AED',
  'VAT',
  'UAE',
  'ID',
  'PAN',
  'TIN',
  'URL',
])

/**
 * Converts a snake_case or SCREAMING_SNAKE_CASE key into Title Case, keeping
 * known domain acronyms all-caps.
 * e.g. "risk_profile_type" → "Risk Profile Type"; "individual_kyc" → "Individual KYC"
 */
export function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\b[A-Za-z]+\b/g, (word) =>
      FORMAT_KEY_ACRONYMS.has(word.toUpperCase()) ? word.toUpperCase() : word,
    )
}

/**
 * Capitalizes the first character of `value`. Rest of the string is
 * preserved as-is (use `capitalize(s.toLowerCase())` for title-case-style
 * single-word output).
 */
export function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value
}

/**
 * Initials from one or more name parts. Joins the first character of each
 * non-empty part, uppercased. Returns `'?'` when nothing useful is provided.
 *
 * @example
 *   getInitials('Ahmed', 'Khan')   // 'AK'
 *   getInitials('Acme Corp Ltd')   // 'A'   (single part)
 *   getInitials(undefined, null)   // '?'
 */
export function getInitials(...parts: Array<string | null | undefined>): string {
  const letters = parts
    .map((part) => part?.trim().charAt(0).toUpperCase() ?? '')
    .filter(Boolean)
    .join('')
  return letters || '?'
}

/** Returns a UUID v4 string suitable for idempotency keys and authUserId. */
export function getIdempotencyKey(): string {
  return crypto.randomUUID()
}
